import type { Selection } from "./charts/utils";
import textContentRaw from "./data/text-content.json";

const MISSING_TEXT = "";

type LevelKey = "level1" | "level2" | "level3" | "level4";

type TextSection = {
    title: string;
    text: string;
};

type ContentEntry = {
    aboveChart: TextSection;
    belowChart: TextSection;
    underLeftBoxText: string;
    effectCard: TextSection;
};

type TextContentStore = Record<LevelKey, Record<string, ContentEntry>>;

const toScopedKey = (topLevel: string, key: string) => (topLevel ? `${topLevel}::${key}` : key);

export interface ContentText {
    aboveChartHeading: string;
    graphDescription: string;
    belowChartHeading: string;
    numbersDescription: string;
    leftBoxText: string;
    effectCardHeading: string;
    effectCardDescription: string;
}

const textContentStore = textContentRaw as TextContentStore;

const createMissingEntry = (): ContentEntry => ({
    aboveChart: {
        title: MISSING_TEXT,
        text: MISSING_TEXT,
    },
    belowChart: {
        title: MISSING_TEXT,
        text: MISSING_TEXT,
    },
    underLeftBoxText: MISSING_TEXT,
    effectCard: {
        title: MISSING_TEXT,
        text: MISSING_TEXT,
    },
});

const normalizeKey = (value: string | null | undefined) => (value ?? "").trim().replace(/\s+/g, " ");

const normalizeText = (value: string | undefined) => {
    const normalized = value?.trim();
    return normalized && normalized.length > 0 ? normalized : MISSING_TEXT;
};

const fallbackLevel1Entry = textContentStore.level1?.Totalt ?? createMissingEntry();

const toLevelKey = (level: number): LevelKey => {
    if (level === 1) {
        return "level1";
    }
    if (level === 2) {
        return "level2";
    }
    if (level === 3) {
        return "level3";
    }
    return "level4";
};

const resolveLevel4Key = (selection: Selection): string => {
    const explicitLevel4 = normalizeKey(selection.level4);
    if (explicitLevel4) {
        return explicitLevel4;
    }

    const selectedLevel3 = normalizeKey(selection.level3);
    if (!selectedLevel3) {
        return "";
    }

    const parentLevel2 = normalizeKey(selection.level2);
    const parentPrefix = parentLevel2 ? `${parentLevel2}_` : "";
    if (parentPrefix && selectedLevel3.startsWith(parentPrefix)) {
        return normalizeKey(selectedLevel3.slice(parentPrefix.length).replaceAll("_", " "));
    }

    const underscoreIndex = selectedLevel3.indexOf("_");
    if (underscoreIndex >= 0) {
        return normalizeKey(selectedLevel3.slice(underscoreIndex + 1).replaceAll("_", " "));
    }

    return selectedLevel3;
};

const buildCandidates = (level: number, selection: Selection): string[] => {
    if (level === 1) {
        return ["Totalt"];
    }
    if (level === 2) {
        return [normalizeKey(selection.level1), "Totalt"];
    }
    if (level === 3) {
        return [normalizeKey(selection.level2), normalizeKey(selection.level1), "Totalt"];
    }
    return [resolveLevel4Key(selection), normalizeKey(selection.level3), normalizeKey(selection.level2), "Totalt"];
};

const getEntryForLevel = (level: number, selection: Selection): ContentEntry => {
    const levelKey = toLevelKey(level);
    const levelEntries = textContentStore[levelKey] || {};
    const candidates = buildCandidates(level, selection);
    const topLevel = normalizeKey(selection.level1);
    const scopedCandidates =
        level >= 2 && topLevel
            ? [...candidates.map((candidate) => (candidate ? toScopedKey(topLevel, candidate) : "")), ...candidates]
            : candidates;
    const uniqueCandidates = Array.from(new Set(scopedCandidates));

    for (const candidate of uniqueCandidates) {
        if (!candidate) {
            continue;
        }
        const matched = levelEntries[candidate];
        if (matched) {
            return matched;
        }
    }

    return fallbackLevel1Entry;
};

const toContentText = (entry: ContentEntry): ContentText => ({
    aboveChartHeading: normalizeText(entry.aboveChart.title),
    graphDescription: normalizeText(entry.aboveChart.text),
    belowChartHeading: normalizeText(entry.belowChart.title),
    numbersDescription: normalizeText(entry.belowChart.text),
    leftBoxText: normalizeText(entry.underLeftBoxText),
    effectCardHeading: normalizeText(entry.effectCard.title),
    effectCardDescription: normalizeText(entry.effectCard.text),
});

const normalizeSelectionArg = (selectionOrTopLevel?: Selection | string | null): Selection => {
    if (typeof selectionOrTopLevel === "string" || selectionOrTopLevel === null) {
        return {
            level1: selectionOrTopLevel,
            level2: null,
            level3: null,
            level4: null,
        };
    }

    if (selectionOrTopLevel && typeof selectionOrTopLevel === "object") {
        return {
            level1: selectionOrTopLevel.level1 ?? null,
            level2: selectionOrTopLevel.level2 ?? null,
            level3: selectionOrTopLevel.level3 ?? null,
            level4: selectionOrTopLevel.level4 ?? null,
        };
    }

    return {
        level1: null,
        level2: null,
        level3: null,
        level4: null,
    };
};

const defaultContent = toContentText(fallbackLevel1Entry);

export const aboveChartHeading = defaultContent.aboveChartHeading;
export const belowChartHeading = defaultContent.belowChartHeading;
export const effectCardHeading = defaultContent.effectCardHeading;
export const effectCardDescription = defaultContent.effectCardDescription;
export const effectCardDescriptionIfZero = "Enova bidrar ikke på dette området.";

export function getContentForLevel(level: number, selectionOrTopLevel?: Selection | string | null): ContentText {
    const normalizedLevel = level >= 1 && level <= 4 ? level : 1;
    const selection = normalizeSelectionArg(selectionOrTopLevel);
    const entry = getEntryForLevel(normalizedLevel, selection);
    return toContentText(entry);
}
